//Quotation.jsx
import React, { useState } from "react";
import {
  Building,
  Factory,
  Layout,
  HomeIcon,
  MapPin,
} from "lucide-react";
import PropTypes from 'prop-types';
import "./quotation.css";
import QuotationModal from './QuotationModal';

const locations = [
  { id: "default", name: "Select Location" },
  { id: "delhi-ncr", name: "Delhi NCR" },
  { id: "uttar-pradesh", name: "Uttar Pradesh" }
];

const services = [
  {
    id: "residential",
    name: "Residential Construction",
    icon: <HomeIcon />,
    description: "Custom homes built with premium materials and attention to detail.",
    features: [
      "Custom Design",
      "Quality Materials",
      "Time-Efficient",
      "Modern Amenities",
    ],
    pricing: {
      "default": {
        basic: 1590,
        classic: 1870,
        premium: 2200,
        royale: 2620
      },
      "delhi-ncr": {
        basic: 1590,
        classic: 1820,
        premium: 2030,
        royale: 2600
      },
      "uttar-pradesh": {
        basic: 1690,
        classic: 2070,
        premium: 3230,
        royale: 5620
      }
    }
  },
  {
    id: "commercial",
    name: "Commercial Construction",
    icon: <Building />,
    description: "State-of-the-art commercial buildings and office spaces.",
    features: [
      "Modern Design",
      "Energy Efficient",
      "Smart Integration",
      "Premium Finishes",
    ],
    pricing: {
      "default": {
        basic: 1890,
        classic: 2170,
        premium: 2430,
        royale: 2720
      },
      "delhi-ncr": {
        basic: 1990,
        classic: 2120,
        premium: 2230,
        royale: 2700
      },
      "uttar-pradesh": {
        basic: 2090,
        classic: 3070,
        premium: 3430,
        royale: 5720
      }
    }
  },
  {
    id: "industrial",
    name: "Industrial Construction",
    icon: <Factory />,
    description: "Large-scale industrial facilities and warehouses.",
    features: [
      "Heavy-duty Construction",
      "Safety Standards",
      "Equipment Integration",
      "Efficient Layout",
    ],
    pricing: {
      "default": {
        basic: 1990,
        classic: 2270,
        premium: 2530,
        royale: 2820
      },
      "delhi-ncr": {
        basic: 2090,
        classic: 2220,
        premium: 2330,
        royale: 2800
      },
      "uttar-pradesh": {
        basic: 2190,
        classic: 3170,
        premium: 3530,
        royale: 5820
      }
    }
  },
  {
    id: "interior",
    name: "Interior Design",
    icon: <Layout />,
    description: "Luxurious interior designs that reflect your style.",
    features: [
      "Custom Layouts",
      "Material Selection",
      "Color Consultation",
      "Furniture Planning",
    ],
    pricing: {
      "default": {
        basic: 1590,
        classic: 1870,
        premium: 2130,
        royale: 2420
      },
      "delhi-ncr": {
        basic: 1690,
        classic: 1820,
        premium: 1930,
        royale: 2400
      },
      "uttar-pradesh": {
        basic: 1790,
        classic: 2770,
        premium: 3130,
        royale: 5420
      }
    }
  }
];

const PriceTable = ({ prices }) => {
  if (!prices) return null;
  
  return (
    <div className="space-y-2 mt-4">
      <div className="grid grid-cols-2 gap-2">
        {Object.entries(prices).map(([type, value]) => (
          <div key={type} className="bg-blue-50 p-2 rounded-lg transform hover:scale-105 transition-transform duration-200">
            <span className="font-semibold text-blue-900 capitalize">{type}</span>
            <div className="text-blue-800 font-medium">₹ {value} /sqft</div>
          </div>
        ))}
      </div>
      <p className="text-xs text-gray-500 mt-1 text-center">(Incl. GST)</p>
    </div>
  );
};

PriceTable.propTypes = {
  prices: PropTypes.shape({
    basic: PropTypes.number.isRequired,
    classic: PropTypes.number.isRequired,
    premium: PropTypes.number.isRequired,
    royale: PropTypes.number.isRequired
  }).isRequired
};

const Quotation = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedService, setSelectedService] = useState(null);
  const [selectedLocation, setSelectedLocation] = useState("default");

  const handleQuotationRequest = (service) => {
    setSelectedService(service);
    setIsModalOpen(true);
  };

  return (
    <>
      <div className="min-h-screen bg-gray-50">
        {/* Header Section */}
        <div className="relative py-16 md:py-24" id="quotation">
          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center space-y-6">
              <h1 className="heading text-4xl lg:text-5xl font-bold mb-4 tracking-tight fade-in">
                Quotation Request
              </h1>
              <p className="text-lg md:text-xl text-blue-600 max-w-2xl mx-auto fade-in">
                Expert construction solutions tailored to your needs
              </p>

              {/* Location Selector */}
              <div className="max-w-xs mx-auto">
                <div className="relative">
                  <MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 text-blue-500 w-5 h-5" />
                  <select
                    value={selectedLocation}
                    onChange={(e) => setSelectedLocation(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 rounded-lg bg-white shadow-md text-gray-800 focus:ring-2 focus:ring-blue-500 focus:outline-none appearance-none cursor-pointer hover:bg-gray-50 transition-colors duration-200"
                  >
                    {locations.map((location) => (
                      <option key={location.id} value={location.id}>
                        {location.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Services Grid */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-20">
          <div className="services-grid grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 md:gap-8">
            {services.map((service, index) => (
              <div
                key={service.id}
                className="service-card bg-white rounded-xl shadow-lg hover:shadow-xl flex flex-col h-full transform hover:-translate-y-1 transition-all duration-300"
                style={{ animationDelay: `${index * 100}ms` }}
              >
                {/* Card Header */}
                <div className="px-6 pt-6 pb-4 border-b border-gray-100">
                  <div className="flex items-start space-x-4">
                    <div className="service-icon flex-shrink-0 p-3 bg-blue-50 rounded-lg text-blue-600">
                      {React.cloneElement(service.icon, {
                        className: "w-6 h-6",
                        strokeWidth: 2,
                      })}
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 leading-tight">
                        {service.name}
                      </h3>
                    </div>
                  </div>
                </div>

                {/* Card Content */}
                <div className="px-6 py-6 flex-grow flex flex-col">
                  <div className="flex-grow">
                    <p className="text-gray-600 text-sm mb-4">
                      {service.description}
                    </p>

                    <div className="space-y-2 mb-4">
                      {service.features.map((feature) => (
                        <div
                          key={feature}
                          className="flex items-center text-sm text-gray-600"
                        >
                          <div className="w-1.5 h-1.5 bg-blue-500 rounded-full mr-2"></div>
                          <span>{feature}</span>
                        </div>
                      ))}
                    </div>

                    <PriceTable prices={service.pricing[selectedLocation]} />
                  </div>

                  <button
                    onClick={() => handleQuotationRequest(service)}
                    className="mt-6 w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-2.5 px-4 rounded-lg transition-all duration-200 flex items-center justify-center space-x-2 hover:shadow-lg"
                  >
                    <span>Get Quote</span>
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Contact Section */}
        <div className="bg-gradient-to-b from-gray-50 to-gray-100 py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-2xl md:text-3xl font-semibold text-gray-900 mb-4">
              Need a Custom Quote?
            </h2>
            <p className="text-gray-600 mb-8 max-w-2xl mx-auto">
              Contact our team for detailed pricing and consultation
            </p>
            <button
              onClick={() => handleQuotationRequest(null)}
              className="inline-flex bg-blue-600 hover:bg-blue-700 text-white font-medium py-3 px-8 rounded-lg transition-all duration-200 items-center justify-center space-x-2 hover:shadow-lg transform hover:-translate-y-0.5"
            >
              <span>Get Custom Quote</span>
            </button>
          </div>
        </div>
      </div>

      <QuotationModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        selectedService={selectedService}
        selectedLocation={selectedLocation}
        locationName={locations.find(loc => loc.id === selectedLocation)?.name}
      />
    </>
  );
};

export default Quotation;